import { useCallback } from "react";
import { setUser } from "@sentry/react";
import { usePostHog } from "posthog-js/react";
import { type AuthUser } from "~/api/auth/AuthUser";
import { DEBUG_TRACKING } from "~/settings";

type TrackingArea = "Ejendomstjek" | "Ejendomme" | "ByggeRadar";

// Ensure tracking events are properly typed to prevent garbage tracking data
type TrackingEvents =
  | {
      event: "user_signed_up";
      properties: { email: string };
    }
  | {
      event: "user_logged_in";
      properties: { email: string };
    }
  | {
      event: "user_confirmed_mfa";
      properties: { email: string };
    }
  | {
      event: "user_logged_out";
      properties: { email: string };
    }
  | {
      event: "user_requested_verification_email";
      properties: { email: string };
    }
  | {
      event: "user_requested_verification_sms";
      properties: { phone: string };
    }
  | {
      event: "user_requested_mfa_code_email";
      properties: { email: string };
    }
  | {
      event: "user_requested_password_reset";
      properties: { email: string };
    }
  | {
      event: "user_resat_password";
      properties: undefined;
    }
  | {
      event: "button_clicked";
      properties: { button_name: string };
    }
  | {
      event: "form_submit_invalid";
      properties: {
        form_name: string;
        // Optional email field for unauthorized tracking - i.e. signup/login.
        email?: string;
        field_errors: { field: string; errors: string[] }[];
      };
    }
  | {
      event: "form_submit_failed";
      properties: { form_name: string; error: unknown };
    }
  | {
      event: "form_submitted";
      properties: { form_name: string };
    }
  | {
      event: "generic_error";
      properties: { message: string };
    }
  | {
      event: "polygon_draw";
      properties: undefined;
    }
  | {
      event: "address_search_result_selected";
      properties:
        | {
            area: TrackingArea;
            address: string;
            access_address_id: string;
            place_id: string | null;
          }
        | { area: "Ejendomme"; bfe: string };
    }
  | {
      event: "address_search_no_result";
      properties: {
        area: TrackingArea;
        search: string;
      };
    }
  | {
      event: "entity_visited";
      properties: {
        area: TrackingArea;
        section: string | null;
        address: string;
        [key: string]: unknown;
      };
    }
  | {
      event: "entity_expanded";
      properties: {
        area: TrackingArea;
        subSection: string;
        address: string;
        [key: string]: string | undefined;
      };
    }
  | {
      event: "tab_selected";
      properties: {
        area: TrackingArea;
        section: string | null;
        tab: string;
        [key: string]: unknown;
      };
    }
  | {
      event: "file_download_request";
      properties: {
        id: string;
        name: string;
      };
    }
  | {
      event: "file_download_failed";
      properties: {
        id: string;
        name: string;
        error: string;
      };
    }
  | {
      event: "analysis_report_read_more_clicked";
      properties: { slug: string; has_subscription: boolean };
    }
  | {
      event: "analysis_report_open_clicked";
      properties: { slug: string; has_subscription: boolean };
    }
  | {
      event: "analysis_report_paywall_shown";
      properties: { slug: string };
    };

export type PersonProperties = {
  byggeradar_access?: boolean;
  agent_count?: number;
  agents_with_notify_count?: number;
  collection_count?: number;
};

type TrackingEvent = { event: string; properties?: unknown };

export const useTracking = <FeatureEvents extends TrackingEvent = never>() => {
  const posthog = usePostHog();

  const identifyUser = (user: AuthUser | null) => {
    if (DEBUG_TRACKING) {
      console.debug("[IdentifyUser]", user);
    }
    if (user) {
      setUser({
        id: user.id,
        email: user.email,
        username: user.fullName,
      });

      if (user.isImpersonated) {
        // Neither party belongs in analytics here: the staff user is not the
        // one browsing, and the customer's profile must not pick up the visit.
        posthog?.reset();
        return;
      }

      const nowISO = new Date().toISOString();

      posthog?.identify(
        // Using the user's id (UUID) as main identifier,
        // email & name is preferred for "display name" in PostHog UI.
        user.id,
        // $set properties - updated with current values
        {
          email: user.email,
          name: user.fullName,
          last_identified_at: nowISO,
        },
        // $set_once properties - set if a value doesn't already exist
        {
          initial_url: window.location.href,
          signed_up_at: nowISO,
        },
      );
    } else {
      posthog.reset();
      setUser(null);
    }
  };

  const trackEvent = useCallback(
    <T extends TrackingEvents | FeatureEvents>(
      event: T["event"],
      properties: T extends { properties: infer P } ? P : undefined,
    ) => {
      posthog?.capture(event, properties);

      if (DEBUG_TRACKING) {
        console.debug("[TrackEvent]", event, properties);
      }
    },
    [posthog],
  );

  const setPersonProperties = useCallback(
    (properties: PersonProperties) => {
      posthog?.setPersonProperties(properties);

      if (DEBUG_TRACKING) {
        console.debug("[SetPersonProperties]", properties);
      }
    },
    [posthog],
  );

  return { identifyUser, trackEvent, setPersonProperties };
};
